'use client';

import { faCalendarDays, IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { tv } from 'tailwind-variants';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { Laboratory, Partner, Professional } from '@medprev-web-app/api-graphql-client';

import { ShareButton } from '@/app/(main)/(pages)/(institucional)/_components/share-button.component';
import { EntityProcedureAnchorButton } from '@/app/(main)/(pages)/_components/entities/entity-procedure/_components/entity-procedure-anchor-button.component';
import { scrollToEntityProcedureSection } from '@/app/(main)/(pages)/_components/entities/entity-procedure/_utils/scroll-to-entity-procedure-section.util';
import { Branch } from '@/app/(main)/(pages)/_types/branch.type';
import { Button } from '@/components/ui/button/button.component';
import { Icon } from '@/components/ui/icon.component';
import { Link } from '@/components/ui/link/link.component';
import { useAgendamentoNavigation } from '@/hooks/navigation/use-agendamento-navigation.hook';
import { useNewSchedule } from '@/feature-flags/hooks/use-new-schedule.hook';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { generateWhatsAppUrl } from '@/utils/formatters/phonenumber';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

const buttonMobileStyles = tv({
  slots: {
    buttonMobile: [
      'w-16 h-16 rounded-full bg-green-500',
      'flex items-center justify-center text-white mb-2',
      'hover:bg-green-600 cursor-pointer',
    ],
  },
});

export function ButtonMobile({
  className,
  icon,
  onClick,
  ariaLabel,
  iconSize = 'xl',
  dataTestid = '',
  'data-analytics-event': dataAnalyticsEvent,
}: {
  className?: string;
  icon: IconDefinition;
  onClick?: () => void;
  ariaLabel?: string;
  iconSize?: SizeProp;
  dataTestid?: string;
  ['data-analytics-event']?: string;
}) {
  const { buttonMobile } = buttonMobileStyles();

  if (onClick) {
    return (
      <button
        type="button"
        aria-label={ariaLabel}
        data-testid={dataTestid}
        className={buttonMobile({ className })}
        onClick={onClick}
        data-analytics-event={dataAnalyticsEvent}
      >
        <Icon icon={icon} size={iconSize} />
      </button>
    );
  }

  return (
    <div
      data-testid={dataTestid}
      className={buttonMobile({ className })}
      data-analytics-event={dataAnalyticsEvent}
    >
      <Icon icon={icon} size={iconSize} />
    </div>
  );
}

interface EntityActionHeaderProps {
  entityType: EntityTypeEnum;
  entity: Professional | Partner | Laboratory | Branch;
  whatsappMessage?: string;
  hasSchedulesSection?: boolean;
  onSelectExams?: () => void;
}

export function EntityActionHeader({
  entityType,
  entity,
  whatsappMessage = '',
  hasSchedulesSection = true,
  onSelectExams,
}: EntityActionHeaderProps) {
  const { buildLink } = useAgendamentoNavigation();
  const { enabled: newScheduleEnabled, loading: isFlagLoading } = useNewSchedule();
  const usesLegacyRedirect = !isFlagLoading && !newScheduleEnabled;

  const isProfessional = entityType === EntityTypeEnum.PROFESSIONAL;
  const showsLegacyScheduleLink = isProfessional && usesLegacyRedirect;
  const showsSchedulesAnchor = isProfessional && !usesLegacyRedirect && hasSchedulesSection;

  const isClinic = entityType === EntityTypeEnum.CLINIC;
  const showsClinicRedirect = isClinic && usesLegacyRedirect;
  const showsClinicAnchor = isClinic && !usesLegacyRedirect && hasSchedulesSection;

  const isLaboratory = entityType === EntityTypeEnum.LABORATORY_COLLECT_POINT;
  const showsLaboratoryRedirect = isLaboratory && usesLegacyRedirect;
  const showsLaboratoryExamsModal = isLaboratory && !usesLegacyRedirect && Boolean(onSelectExams);

  return (
    <>
      <div>
        <div className="hidden xl:flex items-center gap-3" data-testid="entity-xl-action--action-header">
          {showsLegacyScheduleLink && (
            <Button
              startIcon={faCalendarDays}
              className="flex-1"
              data-testid={DataTestIdEnum.PROFESSIONAL_PROFILE_SCHEDULE_BUTTON_HEADER}
              data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_SELECT_REDIRECT}
              asChild
            >
              <Link
                prefetch={false}
                href={buildLink({ path: `/professionals/${entity.id}/` })}
              >
                Consultas e horários
              </Link>
            </Button>
          )}

          {showsClinicRedirect && (
            <Button
              startIcon={faCalendarDays}
              className="flex-1"
              data-analytics-event={AnalyticsEnum.CLICK_PARTNER_REDIRECT_MODAL}
              asChild
            >
              <Link
                prefetch={false}
                href={buildLink({ path: `/parceiros/${entity.id}/` })}
              >
                Serviços e horários
              </Link>
            </Button>
          )}

          {showsClinicAnchor && (
            <EntityProcedureAnchorButton
              className="flex-1"
              label="Atendimentos"
              data-analytics-event={AnalyticsEnum.CLICK_PARTNER_REDIRECT_MODAL}
            />
          )}

          {showsLaboratoryRedirect && (
            <Button
              startIcon={faCalendarDays}
              className="flex-1"
              data-analytics-event={AnalyticsEnum.LABORATORY_PROFILE_SELECT_REDIRECT_EXAMS_AND_SCHEDULES}
              asChild
            >
              <Link
                prefetch={false}
                href={buildLink({ path: `/laboratorio/${entity.id}/` })}
              >
                Exames e horários
              </Link>
            </Button>
          )}

          {showsLaboratoryExamsModal && (
            <Button
              startIcon={faCalendarDays}
              className="flex-1"
              data-testid={DataTestIdEnum.BRANCH_LABORATORY_SELECT_EXAMS_BUTTON}
              data-analytics-event={AnalyticsEnum.LABORATORY_PROFILE_CLICK_SEE_EXAMS}
              onClick={onSelectExams}
            >
              Ver exames
            </Button>
          )}

          {entityType === EntityTypeEnum.BRANCH && 'contacts' in entity && entity.contacts?.whatsapp && (
            <Button
              data-testid={DataTestIdEnum.BRANCH_SCHEDULE_WHATSAPP_BUTTON}
              startIcon={faWhatsapp}
              className="flex-1"
              data-analytics-event={AnalyticsEnum.BRANCH_PROFILE_CLICK_WHATSAPP_SCHEDULE}
              onClick={() =>
                window.open(
                  generateWhatsAppUrl(entity?.contacts?.whatsapp, whatsappMessage),
                  '_blank',
                  'noopener,noreferrer',
                )}
            >
              Quero agendar
            </Button>
          )}

          <ShareButton entityType={entityType} entity={entity} />
        </div>

        <div
          className="hidden xsm:flex xl:hidden items-center justify-between gap-3 w-full"
          data-testid="entity-md-action--action-header"
        >
          {(showsLegacyScheduleLink || showsSchedulesAnchor) && (showsLegacyScheduleLink
            ? (
                <Button
                  startIcon={faCalendarDays}
                  className="w-full"
                  data-testid={DataTestIdEnum.PROFESSIONAL_PROFILE_SCHEDULE_BUTTON_HEADER}
                  data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_SELECT_REDIRECT}
                  asChild
                >
                  <Link
                    prefetch={false}
                    href={buildLink({ path: `/professionals/${entity.id}/` })}
                  >
                    Consultas e horários
                  </Link>
                </Button>
              )
            : (
                <EntityProcedureAnchorButton
                  className="w-full lg:hidden"
                  data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_SELECT_REDIRECT}
                />
              ))}

          {showsClinicRedirect && (
            <Button
              startIcon={faCalendarDays}
              className="w-full"
              data-analytics-event={AnalyticsEnum.CLICK_PARTNER_REDIRECT_MODAL}
              asChild
            >
              <Link
                prefetch={false}
                href={buildLink({ path: `/parceiros/${entity.id}/` })}
              >
                Serviços e horários
              </Link>
            </Button>
          )}

          {showsClinicAnchor && (
            <EntityProcedureAnchorButton
              className="w-full"
              label="Atendimentos"
              data-analytics-event={AnalyticsEnum.CLICK_PARTNER_REDIRECT_MODAL}
            />
          )}

          {showsLaboratoryRedirect && (
            <Button
              startIcon={faCalendarDays}
              className="w-full"
              data-analytics-event={AnalyticsEnum.LABORATORY_PROFILE_SELECT_REDIRECT_EXAMS_AND_SCHEDULES}
              asChild
            >
              <Link
                prefetch={false}
                href={buildLink({ path: `/laboratorio/${entity.id}/` })}
              >
                Exames e horários
              </Link>
            </Button>
          )}

          {showsLaboratoryExamsModal && (
            <Button
              startIcon={faCalendarDays}
              className="w-full"
              data-testid={DataTestIdEnum.BRANCH_LABORATORY_SELECT_EXAMS_BUTTON}
              data-analytics-event={AnalyticsEnum.LABORATORY_PROFILE_CLICK_SEE_EXAMS}
              onClick={onSelectExams}
            >
              Ver exames
            </Button>
          )}

          {entityType === EntityTypeEnum.BRANCH && 'contacts' in entity && (
            <Button
              data-testid={DataTestIdEnum.BRANCH_SCHEDULE_WHATSAPP_BUTTON}
              startIcon={faWhatsapp}
              className="w-full"
              data-analytics-event={AnalyticsEnum.BRANCH_PROFILE_CLICK_WHATSAPP_SCHEDULE}
              onClick={() =>
                window.open(
                  generateWhatsAppUrl(entity?.contacts?.whatsapp, whatsappMessage),
                  '_blank',
                  'noopener,noreferrer',
                )}
            >
              Quero agendar
            </Button>
          )}

          <div className="flex items-center gap-3">
            <ShareButton entityType={entityType} entity={entity} />
          </div>
        </div>

        <div className="grid grid-cols-3 xsm:hidden w-full" data-testid={DataTestIdEnum.ENTITY_SM_ACTION_HEADER}>
          <div className="flex flex-col items-center">
            {(showsLegacyScheduleLink || showsSchedulesAnchor) && (
              <>
                {showsLegacyScheduleLink
                  ? (
                      <Link
                        prefetch={false}
                        href={buildLink({ path: `/professionals/${entity.id}/` })}
                        data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_SELECT_REDIRECT}
                      >
                        <ButtonMobile icon={faCalendarDays} />
                      </Link>
                    )
                  : (
                      <ButtonMobile
                        icon={faCalendarDays}
                        ariaLabel="Consultas e horários"
                        dataTestid={DataTestIdEnum.ENTITY_PROCEDURE_SECTION_ANCHOR_BUTTON}
                        data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_SELECT_REDIRECT}
                        onClick={scrollToEntityProcedureSection}
                      />
                    )}
                <span className="text-green-500 font-bold text-center">
                  Consultas e
                  <br />
                  {' '}
                  horários
                </span>
              </>
            )}

            {showsClinicRedirect && (
              <>
                <Link
                  prefetch={false}
                  href={buildLink({ path: `/parceiros/${entity.id}/` })}
                  data-analytics-event={AnalyticsEnum.CLICK_PARTNER_REDIRECT_MODAL}
                >
                  <ButtonMobile icon={faCalendarDays} />
                </Link>
                <span className="text-green-500 font-bold text-center">
                  Serviços e
                  <br />
                  {' '}
                  horários
                </span>
              </>
            )}

            {showsClinicAnchor && (
              <>
                <ButtonMobile
                  icon={faCalendarDays}
                  ariaLabel="Atendimentos"
                  dataTestid={DataTestIdEnum.ENTITY_PROCEDURE_SECTION_ANCHOR_BUTTON}
                  data-analytics-event={AnalyticsEnum.CLICK_PARTNER_REDIRECT_MODAL}
                  onClick={scrollToEntityProcedureSection}
                />
                <span className="text-green-500 font-bold text-center">Atendimentos</span>
              </>
            )}

            {showsLaboratoryRedirect && (
              <>
                <Link
                  prefetch={false}
                  href={buildLink({ path: `/laboratorio/${entity.id}/` })}
                  data-analytics-event={AnalyticsEnum.LABORATORY_PROFILE_SELECT_REDIRECT_EXAMS_AND_SCHEDULES}
                >
                  <ButtonMobile icon={faCalendarDays} />
                </Link>
                <span className="text-green-500 font-bold text-center">
                  Exames e
                  <br />
                  {' '}
                  horários
                </span>
              </>
            )}

            {showsLaboratoryExamsModal && (
              <>
                <ButtonMobile
                  icon={faCalendarDays}
                  ariaLabel="Ver exames"
                  dataTestid={DataTestIdEnum.BRANCH_LABORATORY_SELECT_EXAMS_BUTTON}
                  data-analytics-event={AnalyticsEnum.LABORATORY_PROFILE_CLICK_SEE_EXAMS}
                  onClick={onSelectExams}
                />
                <span className="text-green-500 font-bold text-center">Ver exames</span>
              </>
            )}

            {entityType === EntityTypeEnum.BRANCH && 'contacts' in entity && (
              <>
                <ButtonMobile
                  dataTestid={DataTestIdEnum.BRANCH_SCHEDULE_WHATSAPP_BUTTON}
                  icon={faWhatsapp}
                  ariaLabel="Quero agendar"
                  data-analytics-event={AnalyticsEnum.BRANCH_PROFILE_CLICK_WHATSAPP_SCHEDULE}
                  onClick={() =>
                    window.open(
                      generateWhatsAppUrl(entity?.contacts?.whatsapp, whatsappMessage),
                      '_blank',
                      'noopener,noreferrer',
                    )}
                />
                <span className="text-green-500 font-bold text-center">
                  Quero
                  <br />
                  {' '}
                  agendar
                </span>
              </>
            )}
          </div>

          <div>
            <ShareButton entityType={entityType} entity={entity} />
          </div>
        </div>
      </div>
    </>
  );
}
